// Incoming files panel, shared by the link and nearby views: file list, download, progress,
// then save / share per file or all at once (zip, or the system share sheet).

import type { DropReceiver, IncomingFile } from '../drop/receiver';
import { formatBytes } from '../drop/protocol';
import { zipFiles } from '../drop/zip';
import { h, keep, progressBar } from './dom';
import { canShareFiles, keepAwake, saveFile, shareFiles } from './common';

export function incomingPanel(receiver: DropReceiver, { autoStart = false } = {}): { el: HTMLElement; destroy(): void } {
  const el = h('section', { class: 'card' });
  let started = false;
  let notice: string | null = null;
  let error: string | null = null;
  let release: (() => void) | null = null;
  let frame = 0;

  const start = () => {
    if (started) return;
    started = true;
    release = keepAwake();
    receiver
      .downloadAll()
      .catch((e: Error) => (error = e.message))
      .finally(() => {
        release?.();
        release = null;
        render();
      });
    render();
  };

  const schedule = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(render);
  };

  function fileRow(f: IncomingFile): HTMLElement {
    const status =
      f.status === 'done' ? 'reçu' : f.status === 'error' ? (f.error ?? 'erreur') : f.status === 'receiving' ? `${Math.floor((f.received / Math.max(1, f.size)) * 100)} %` : 'en attente';
    const actions =
      f.status === 'done' && f.file
        ? h(
            'div',
            { class: 'row' },
            h('button', { class: 'btn small', text: 'Enregistrer', onclick: () => saveFile(f.file!) }),
            canShareFiles([f.file]) && h('button', { class: 'btn small', text: 'Partager…', onclick: () => void shareFiles([f.file!]) }),
          )
        : null;
    return h(
      'li',
      { class: 'file' },
      h('div', { class: 'file-head' }, h('span', { class: 'file-name', text: f.name }), h('span', { class: 'dim', text: formatBytes(f.size) })),
      f.status === 'receiving' && progressBar(f.received / Math.max(1, f.size)),
      h('div', { class: `file-status ${f.status === 'error' ? 'bad' : 'dim'}`, text: status }),
      actions,
    );
  }

  async function saveAll(button: HTMLButtonElement) {
    button.disabled = true;
    button.textContent = 'Préparation du zip…';
    try {
      const files = receiver.files.map((f) => f.file!).filter(Boolean);
      saveFile(await zipFiles(files, `fichiers-${new Date().toISOString().slice(0, 10)}.zip`, receiver.key));
    } catch (e) {
      error = `zip : ${(e as Error).message}`;
    }
    render();
  }

  function render() {
    const files = receiver.files;
    const done = files.filter((f) => f.file).map((f) => f.file!);
    const total = receiver.totalSize;
    el.replaceChildren(
      ...keep(
      h('h2', { text: `De ${receiver.from}` }),
      h('p', { class: 'dim', text: `${files.length} fichier${files.length > 1 ? 's' : ''} · ${formatBytes(total)}` }),
      notice && h('p', { class: 'warn', text: notice }),
      error && h('p', { class: 'bad', text: error }),
      !started && h('button', { class: 'btn primary', text: `Télécharger (${formatBytes(total)})`, onclick: start }),
      started && !receiver.complete && !receiver.cancelled && h('div', { class: 'stack' }, progressBar(receiver.received / Math.max(1, total)), h('p', { class: 'dim', text: `${formatBytes(receiver.received)} / ${formatBytes(total)}` })),
      receiver.complete && files.length > 1 && h('button', { class: 'btn primary', text: 'Tout enregistrer (.zip)', onclick: (e: Event) => void saveAll(e.currentTarget as HTMLButtonElement) }),
      receiver.complete && files.length > 1 && canShareFiles(done) && h('button', { class: 'btn', text: 'Tout partager…', onclick: () => void shareFiles(done) }),
      h('ul', { class: 'files' }, ...files.map(fileRow)),
      ),
    );
  }

  const listeners: [string, EventListener][] = [
    ['change', schedule],
    ['sender-gone', () => ((notice = 'L’expéditeur s’est déconnecté. Garde cette page ouverte : le transfert reprendra s’il revient.'), render())],
    ['sender-back', () => ((notice = null), render())],
    ['cancelled', () => ((notice = 'L’expéditeur a annulé l’envoi.'), render())],
  ];
  for (const [type, fn] of listeners) receiver.addEventListener(type, fn);
  render();
  if (autoStart) start();

  return {
    el,
    destroy() {
      cancelAnimationFrame(frame);
      for (const [type, fn] of listeners) receiver.removeEventListener(type, fn);
      release?.();
    },
  };
}
